/**
 * Reads one policy.
 *
 * A single-document read, unlike the list, needs no query constraint: the
 * rules test `resource.data.status` against the document itself, which exists
 * by the time the rule runs. So this can just `getDoc` and let the rules
 * refuse it — and a refusal is the correct answer for a viewer opening a
 * draft's URL directly.
 */

import { calendarDate } from '../../shared/dates';
import { useQuery } from '@tanstack/react-query';
import { doc, getDoc, getFirestore, collection, getDocs } from 'firebase/firestore';
import { app } from '../../lib/firebase';
import type { Policy } from '../../types/policy';
import { toPolicy } from './usePolicies';
import { toMomentString } from './useReviewHistory';

const db = getFirestore(app);

/** A file attached to a policy. */
export interface Attachment {
  id: string;
  name: string;
  contentType: string;
  /** Bytes. Null when the stored document omits it. */
  size: number | null;
  storagePath: string | null;
  /** A download URL, which is what the viewer needs. Null when absent. */
  url: string | null;
  /** The attachment this one was converted from — a PDF made from a .docx. */
  generatedFrom: string | null;
}

export const DOCX_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export const isPdf = (a: Attachment) => a.contentType === 'application/pdf' || /\.pdf$/i.test(a.name);
export const isDocx = (a: Attachment) => a.contentType === DOCX_TYPE || /\.docx$/i.test(a.name);

/** A set of files, and which one of them is the document. */
export interface FileSet {
  attachments: Attachment[];
  documentId: string | null;
}

/**
 * The file this set is OF — by name, not by position (blocker B6).
 *
 * `readableAttachment` used to take the first PDF in the array. Replacing a
 * document appends the new file and removes the old ones afterwards, so a
 * removal that failed left the OLD file first and therefore the one on screen
 * and the one published, with nothing to say so. A set written since carries
 * the id of its document and position means nothing. A set written before
 * carries no name, and the old rule stands for it, so nothing already stored
 * changes meaning.
 */
export function authoritative(set: FileSet): Attachment | null {
  if (set.documentId) {
    const named = set.attachments.find((a) => a.id === set.documentId);
    if (named) return named;
  }
  return set.attachments.find(isPdf) ?? set.attachments[0] ?? null;
}

/** The file staff read: the document's PDF, or the document when it is one. */
export function readableAttachment(set: FileSet): Attachment | null {
  const doc = authoritative(set);
  if (!doc) return null;
  if (isPdf(doc)) return doc;
  return set.attachments.find((a) => a.generatedFrom === doc.id && isPdf(a)) ?? null;
}

/** The Word original of this set's document, when there is one. */
export function wordOriginal(set: FileSet): Attachment | null {
  const doc = authoritative(set);
  if (!doc) return null;
  if (isDocx(doc)) return doc;
  if (!doc.generatedFrom) return null;
  return set.attachments.find((a) => a.id === doc.generatedFrom && isDocx(a)) ?? null;
}

/** An approver's last word on a draft that was sent back. */
export interface LastDecision {
  /** `withdrawn`: the author pulled it back out of approval; nobody rejected anything. */
  type: 'rejected' | 'requestChanges' | 'withdrawn';
  comments: string | null;
  byName: string | null;
  stepName: string | null;
  /** ISO date. */
  at: string | null;
}

/**
 * WHERE THIS POLICY CAME FROM, WHEN IT CAME BY MIGRATION.
 *
 * Written onto the policy at import (`importSource`), not held only on the
 * import job, because a job record can be deleted and a document whose history
 * cannot be answered for is no evidence at all. Every field is optional:
 * policies imported before this existed carry some of it or none, and the
 * screen shows what is known rather than inventing the rest.
 */
export interface ImportProvenance {
  importedAt: string | null;
  importedBy: string | null;
  importedByName: string | null;
  importJobId: string | null;
  sourceFileName: string | null;
  sourceFileSha256: string | null;
  sourceRowNumber: number | null;
  legacyId: string | null;
  originalPolicyNumber: string | null;
  versionsImported: number | null;
}

export interface PolicyDetail extends Policy {
  /** Set only for a policy that arrived by migration. */
  importedFrom: ImportProvenance | null;
  summary: string | null;
  notes: string | null;
  /** The body, when the policy is text rather than a file. The server accepts
   *  a submission with either, so the page must not demand a file. */
  content: string | null;
  categoryName: string | null;
  documentTypeName: string | null;
  tags: string[];
  attachments: Attachment[];
  /** Which attachment is the document. Null on records written before B6. */
  documentId: string | null;
  requiresAcknowledgement: boolean;
  acknowledgementDueDays: number | null;
  reviewCycleMonths: number | null;
  effectiveDate: string | null;
  versionNumber: number | null;
  /** The published version's number; null until first publication. */
  currentVersionNumber: number | null;
  currentVersionId: string | null;
  ownerId: string | null;
  /**
   * The rest of what the server counts as "whose policy this is", and the two
   * flags that make several actions impossible. The page could not see any of
   * them, so it offered actions to the wrong people and hid them from the
   * right ones. See `policyCapabilities.ts`.
   */
  createdBy: string | null;
  ownerUserId: string | null;
  revisionStartedBy: string | null;
  coOwnerIds: string[];
  /** Imported or externally managed content: several callables refuse it. */
  readOnly: boolean;
  source: string | null;
  /** Who submitted the round that is open, mirrored from the approval. */
  submittedBy: string | null;
  categoryId: string | null;
  documentTypeId: string | null;
  departmentIds: string[];
  regulatoryStandardIds: string[];
  targetAudience: 'all' | 'departments' | 'roles';
  targetDepartmentIds: string[];
  targetRoles: string[];
  pendingChangeSummary: string | null;
  lastDecision: LastDecision | null;
  /** Retirement, as the server recorded it. Nothing here is invented. */
  retiredAt: string | null;
  retiredReason: string | null;
  retiredByUid: string | null;
  retiredByName: string | null;
  supersededById: string | null;
  /**
   * A publication the system owed and could not finish, and an audience it
   * could not finish asking. Absent on every policy that owes nothing.
   */
  publicationFailure: { attempts: number; lastError: string | null; lastAttemptAt: string | null; dueAt: string | null } | null;
  acknowledgementAssignment: { state: string; assigned: number | null; expected: number | null; lastError: string | null } | null;
  /**
   * WHETHER SEARCH HAS BEEN TOLD ABOUT THIS POLICY. Null once it has.
   *
   * The index is a projection, not the truth, so a failure to update it never
   * blocks anything. It used to be invisible as well, which meant a policy
   * could be published, correct, and unfindable by search with nothing
   * anywhere saying so. An hourly job retries; this is what the page shows an
   * administrator while it is still owed.
   */
  searchIndex: { state: string; attempts: number; lastError: string | null; at: string | null } | null;
  /** Approved and waiting for this date, when the workflow holds documents until they take effect. ISO date. */
  heldUntil?: string | null;
  /** True when the caller may not read it, as opposed to it not existing. */
  forbidden?: boolean;
}

function toLastDecision(value: unknown): LastDecision | null {
  if (typeof value !== 'object' || value === null) return null;
  const v = value as Record<string, unknown>;
  const type = v.type === 'rejected' || v.type === 'requestChanges' || v.type === 'withdrawn' ? v.type : null;
  if (!type) return null;
  return {
    type,
    comments: str(v.comments),
    byName: str(v.byName) ?? str(v.byEmail),
    stepName: str(v.stepName),
    at: toIsoDate(v.at),
  };
}

/** A publication the system owes. Null unless one is recorded. */
function toFailure(v: unknown): PolicyDetail['publicationFailure'] {
  if (typeof v !== 'object' || v === null) return null;
  const f = v as Record<string, unknown>;
  const attempts = num(f.attempts) ?? 0;
  // A pending transition with no failed attempt is simply work in flight.
  if (attempts < 1) return null;
  return {
    attempts,
    lastError: str(f.lastError),
    lastAttemptAt: toIsoDate(f.lastAttemptAt),
    dueAt: toIsoDate(f.dueAt),
  };
}

/**
 * Search's copy, while it is still behind. Null when it has settled, and null
 * while it is merely in flight.
 *
 * `owed` is written a second before `done` on every ordinary publication, so
 * reporting it immediately would put "search has not been updated" on the page
 * for a moment on every publish, which is noise rather than information. It is
 * worth saying only once the work has actually failed, or has sat owed for
 * longer than the retry window. `SEARCH_UNRESOLVED_AFTER_MS` mirrors
 * `RETRY_AFTER_MS` in functions/src/algolia/searchProjection.ts.
 */
const SEARCH_UNRESOLVED_AFTER_MS = 5 * 60 * 1000;

export function toSearchIndex(v: unknown, now = Date.now()): PolicyDetail['searchIndex'] {
  if (typeof v !== 'object' || v === null) return null;
  const m = v as Record<string, unknown>;
  const state = str(m.state);
  if (!state || state === 'done') return null;

  const at = toMomentString(m.at);
  if (state === 'owed' && at && now - new Date(at).getTime() < SEARCH_UNRESOLVED_AFTER_MS) return null;

  return { state, attempts: num(m.attempts) ?? 0, lastError: str(m.lastError), at: toIsoDate(m.at) };
}

/** How far publication got in asking the audience to read it. */
function toAssignment(v: unknown): PolicyDetail['acknowledgementAssignment'] {
  if (typeof v !== 'object' || v === null) return null;
  const a = v as Record<string, unknown>;
  const state = str(a.state);
  if (!state || state === 'done') return null;
  return { state, assigned: num(a.assigned), expected: num(a.expected), lastError: str(a.lastError) };
}

function toStrings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((t): t is string => typeof t === 'string') : [];
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** A date-only value, read in UTC. One rule, in shared/dates.ts. */
export const toIsoDate = calendarDate;

export function toAttachments(value: unknown): Attachment[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((raw, i) => {
    if (typeof raw !== 'object' || raw === null) return [];
    const a = raw as Record<string, unknown>;
    const name = str(a.name) ?? str(a.fileName);
    if (!name) return [];
    return [{
      id: str(a.id) ?? `attachment-${i}`,
      name,
      contentType: str(a.contentType) ?? 'application/octet-stream',
      size: num(a.size),
      storagePath: str(a.storagePath),
      // `addDraftAttachment` stores the download URL as `url`; older
      // documents wrote `downloadUrl`. Both are read.
      url: str(a.url) ?? str(a.downloadUrl),
      generatedFrom: str(a.generatedFrom),
    }];
  });
}

async function loadOwnerNames(orgId: string): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  try {
    const snap = await getDocs(collection(db, 'orgs', orgId, 'members'));
    snap.docs.forEach((d) => {
      const data = d.data();
      const name = typeof data.displayName === 'string' ? data.displayName : data.email;
      if (typeof name === 'string' && name) map.set(d.id, name);
    });
  } catch {
    // Not fatal: the owner simply renders as unresolved.
  }
  return map;
}

/**
 * The departments a policy is filed under, from whichever shape it was
 * written in. Older and imported policies hold one department as an embedded
 * object with the list empty. Read as "none", the Edit details form showed
 * no department and a save that added one replaced the one it had.
 * The same rule as `idsOf` in functions/src/policies/listFields.ts.
 */
export function departmentIdsOf(d: Record<string, unknown>): string[] {
  const ids = toStrings(d.departmentIds).map((x) => x.trim()).filter(Boolean);
  if (ids.length) return ids;
  const embedded = d.department;
  const id = typeof embedded === 'object' && embedded !== null ? (embedded as { id?: unknown }).id : null;
  return typeof id === 'string' && id.trim() ? [id.trim()] : [];
}

export function usePolicy(orgId: string | null, policyId: string | undefined) {
  return useQuery({
    queryKey: ['policy', orgId, policyId],
    enabled: Boolean(orgId && policyId),
    // A permission-denied here is a real answer, not a transient fault, and
    // retrying it just delays the page telling the user what happened.
    retry: false,
    queryFn: async (): Promise<PolicyDetail | null> => {
      const [snap, ownerNames] = await Promise.all([
        getDoc(doc(db, 'orgs', orgId!, 'policies', policyId!)),
        loadOwnerNames(orgId!),
      ]);

      if (!snap.exists()) return null;
      const d = snap.data();
      const base = toPolicy(snap.id, d, ownerNames);

      const src = (d.importSource ?? null) as Record<string, unknown> | null;
      return {
        ...base,
        importedFrom: d.isImported === true
          ? {
            importedAt: toIsoDate(src?.importedAt) ?? null,
            importedBy: str(src?.importedBy),
            importedByName: str(src?.importedBy) ? ownerNames.get(String(src!.importedBy)) ?? null : null,
            // The job id is kept on the policy itself, so it survives the job.
            importJobId: str(src?.importJobId) ?? str(d.importJobId),
            sourceFileName: str(src?.sourceFileName),
            sourceFileSha256: str(src?.sourceFileSha256),
            sourceRowNumber: num(src?.sourceRowNumber),
            legacyId: str(src?.legacyId) ?? str(d.legacyId),
            originalPolicyNumber: str(src?.originalPolicyNumber),
            versionsImported: num(src?.versionsImported),
          }
          : null,
        summary: str(d.summary),
        notes: str(d.notes),
        content: str(d.content),
        categoryName: str(d.categoryName),
        documentTypeName: str(d.documentTypeName),
        tags: toStrings(d.tagNames).length ? toStrings(d.tagNames) : toStrings(d.tags),
        attachments: toAttachments(d.attachments),
        documentId: str(d.documentId),
        requiresAcknowledgement: d.requiresAcknowledgement === true,
        acknowledgementDueDays: num(d.acknowledgementDueDays),
        reviewCycleMonths: num(d.reviewIntervalMonths) ?? num(d.reviewCycleMonths),
        effectiveDate: toIsoDate(d.effectiveDate),
        versionNumber: num(d.currentVersionNumber) ?? num(d.draftVersionNumber),
        currentVersionNumber: num(d.currentVersionNumber),
        currentVersionId: str(d.currentVersionId),
        ownerId: str(d.ownerId) ?? str(d.ownerUserId),
        createdBy: str(d.createdBy),
        ownerUserId: str(d.ownerUserId),
        revisionStartedBy: str(d.revisionStartedBy),
        coOwnerIds: toStrings(d.coOwnerIds),
        readOnly: d.readOnly === true,
        source: str(d.source),
        submittedBy: str(d.submittedBy),
        categoryId: str(d.categoryId),
        documentTypeId: str(d.documentTypeId),
        departmentIds: departmentIdsOf(d),
        regulatoryStandardIds: toStrings(d.regulatoryStandardIds),
        targetAudience: d.targetAudience === 'departments' || d.targetAudience === 'roles' ? d.targetAudience : 'all',
        targetDepartmentIds: toStrings(d.targetDepartmentIds),
        targetRoles: toStrings(d.targetRoles),
        pendingChangeSummary: str(d.pendingChangeSummary),
        lastDecision: toLastDecision(d.lastDecision),
        retiredAt: toIsoDate(d.retiredAt),
        heldUntil: toIsoDate(d.heldUntil),
        retiredReason: str(d.retiredReason),
        retiredByUid: str(d.retiredBy),
        retiredByName: ownerNames.get(str(d.retiredBy) ?? '') ?? null,
        supersededById: str(d.supersededById),
        publicationFailure: toFailure(d.releaseFailure) ?? toFailure(d.pendingTransition),
        searchIndex: toSearchIndex(d.searchIndex),
        acknowledgementAssignment: toAssignment(d.ackAssignment),
      };
    },
  });
}

/**
 * THE NEXT VERSION'S FILES, WHICH ARE NOT ON THE POLICY (blocker B2).
 *
 * A revision keeps the policy published, so every member can read the policy
 * document — and it used to carry the unapproved revision's files, each with a
 * permanent download URL. A viewer who opened their own policy record had the
 * draft. The files now live in `policies/{policyId}/revision/current`, which
 * the rules open to authors, reviewers, approvers and administrators only, so
 * a viewer asking for them is refused by the database rather than by this
 * page choosing not to ask.
 *
 * A refusal is the correct answer and is reported as an empty set, not an
 * error: a viewer has no revision, and the record page has nothing to say
 * about one.
 */
export function useRevisionFiles(orgId: string | null, policyId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['policy-revision', orgId, policyId],
    enabled: Boolean(orgId && policyId && enabled),
    retry: false,
    queryFn: async (): Promise<FileSet> => {
      try {
        const snap = await getDoc(doc(db, 'orgs', orgId!, 'policies', policyId!, 'revision', 'current'));
        if (!snap.exists()) return { attachments: [], documentId: null };
        const d = snap.data();
        return { attachments: toAttachments(d.attachments), documentId: str(d.documentId) };
      } catch {
        return { attachments: [], documentId: null };
      }
    },
  });
}
